import { Box, Button, Typography, Avatar } from '@mui/material';
import * as R from 'ramda';
import { FC, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { CardMonthlyBudget, NavItem, SelectTenant, SubNavs } from './components';

import { Activity, AvatarDefault, BarChart, Chart, CheckSquare, Colors, Flag, Home, Layers, LifeBouy, LogoText, Users } from '@/assets';
import { InputText } from '@/components';
import { AuthActions, getAvatarUrlOfUser, getFullNameOfUser, useAuth } from '@/features/auth';
import { useOuterClick } from '@/hooks';
import { Screens } from '@/routes';
import { INavItem } from '@/types';

const SIDE_BAR_WIDTH = 280;

const NAVS_TOP = [
    {
        icon: <Home />,
        title: 'Home',
        path: '/#',
        subNavs: [
            {
                title: 'Overview',
                icon: <Activity />,
                path: '/#'
            },
            {
                title: 'Notifications',
                icon: <i className="fal fa-bell" />,
                path: '/#',
                news: 12
            }
        ]
    },
    {
        icon: <Chart style={{ width: '2rem' }} />,
        title: 'Dashboard',
        news: 8,
        path: '/dashboard'
    },
    {
        icon: <Layers />,
        title: 'Projects',
        path: '/projects'
    },
    {
        icon: <CheckSquare style={{ width: '2.1rem' }} />,
        title: 'Item Libary',
        path: '/item-library'
    },
    {
        icon: <i style={{ width: '2.1rem' }} className="far fa-file-invoice-dollar"></i>,
        title: 'Quoting',
        path: '/quoting'
    },
    {
        icon: <i className="far fa-shopping-cart"></i>,
        title: 'Orders',
        path: '/orders'
    },
    {
        icon: <i className="far fa-address-book"></i>,
        title: 'Suppliers',
        path: '/suppliers'
    },
    {
        icon: <i className="far fa-analytics"></i>,
        title: 'Analytics',
        path: '/#'
    },
    {
        icon: <i className="far fa-comments"></i>,
        title: 'Messages',
        path: '/messages'
    }
];

const NAVS_BOTTOM = [
    {
        icon: <LifeBouy />,
        title: 'Support',
        path: '/support'
    },
    {
        icon: <i className="fal fa-cog" />,
        title: 'Settings',
        path: Screens.Settings
    }
];

interface IProps {}
export const SidebarLayout: FC<IProps> = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [subNavsShow, setSubNavsShow] = useState<INavItem[] | undefined>();

    const form = useForm();
    const { user } = useAuth();

    const innerRef = useOuterClick(() => {
        setSubNavsShow(undefined);
    });

    const onChangeSubNavsShow = (newValue: INavItem[]) => {
        if (R.equals(newValue, subNavsShow)) {
            setSubNavsShow(undefined);
            return;
        }
        setSubNavsShow(newValue);
    };

    const logout = () => {
        dispatch(AuthActions.logout());
    };

    const isMessagePage = pathname.includes(Screens.Messages);

    return (
        <FormProvider {...form}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '100vh' }}>
                <Box
                    ref={innerRef}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        width: subNavsShow ? SIDE_BAR_WIDTH * 2 : SIDE_BAR_WIDTH,
                        transition: 'all 0.15s ease-in-out'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            width: SIDE_BAR_WIDTH,
                            zIndex: 3,
                            backgroundColor: Colors.white,
                            px: 2,
                            py: 4,
                            borderRight: '1px solid',
                            borderColor: Colors.greenWhite,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: '100%',
                            overflow: 'auto'
                        }}
                    >
                        <Box sx={{}}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: 1, width: '100%' }}>
                                <LogoText />
                                <SelectTenant />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
                                {NAVS_TOP.map((item, index) => {
                                    const isActive = pathname.includes(item.path);
                                    return (
                                        <NavItem
                                            key={index}
                                            item={item}
                                            active={isActive}
                                            onClick={() => (item.subNavs ? onChangeSubNavsShow(item.subNavs) : navigate(item.path))}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', my: 3 }}>
                                {NAVS_BOTTOM.map((item, index) => {
                                    const isActive = pathname.includes(item.path);
                                    return <NavItem onClick={() => navigate(item.path)} key={index} item={item} active={isActive} />;
                                })}
                            </Box>
                            <CardMonthlyBudget />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    justifyContent: 'space-between',
                                    borderTop: '1px solid',
                                    borderColor: Colors.greenWhite,
                                    pt: 3,
                                    mt: 3
                                }}
                            >
                                {user && (
                                    <>
                                        <Avatar sx={{ width: 40, height: 40 }} src={getAvatarUrlOfUser(user)} alt="" />
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'normal', color: Colors.tangaroaBlue }}>
                                                {getFullNameOfUser(user)}
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'normal', color: Colors.lynchBlue }}>
                                                {user.email}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                                <Button onClick={logout} sx={{ minWidth: 0, boxShadow: 'none', color: Colors.lynchBlue }}>
                                    <i className="fal fa-sign-out" style={{ fontSize: '1.8rem' }} />
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                    <SubNavs navs={subNavsShow} onClose={() => setSubNavsShow(undefined)} />
                </Box>
                <Box sx={{ flex: 1, p: isMessagePage ? 0 : 4, height: '100%', overflow: 'auto', background: Colors.vistaWhite }}>
                    {children}
                </Box>
            </Box>
        </FormProvider>
    );
};
